import React, { useState } from "react";
import axios from "axios";
import { DropdownItem } from "reactstrap";
import { getCookie } from "../jwt/_helpers/cookie";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const ExportLabelCopy = ({ id, name }) => {
  const tokenAPI = getCookie("token");
  const [exportingLabelCopy, setExportingLabelCopy] = useState(false);

  const escapeCsvValue = (value) => {
    const safeValue = value === null || value === undefined ? "" : String(value);
    return `"${safeValue.replace(/"/g, '""')}"`;
  };

  const toCsvBoolean = (value) => value ? "TRUE" : "FALSE";

  const getGenreName = (genreId, genres) => {
    if (!genreId) {
      return "";
    }

    const genre = genres.find((item) => item.id === genreId);
    return genre ? genre.name : "";
  };

  const getSubgenreName = (genreId, subgenreId, genres) => {
    if (!genreId || !subgenreId) {
      return "";
    }

    const genre = genres.find((item) => item.id === genreId);
    if (!genre || !genre.subgenres) {
      return "";
    }

    const subgenre = genre.subgenres.find((item) => item.id === subgenreId);
    return subgenre ? subgenre.name : "";
  };

  const getArtistNamesByKind = (artists, kind) => {
    return (artists || [])
      .filter((artist) => artist.kind === kind)
      .map((artist) => artist.artist && artist.artist.name ? artist.artist.name : "")
      .filter(Boolean)
      .join(" | ");
  };

  const getContributorNamesByRole = (contributors, roleId) => {
    return (contributors || [])
      .filter((contributor) => {
        const contributorRoleId = contributor.role && contributor.role.id ? String(contributor.role.id).toLowerCase() : "";
        const contributorRoleName = contributor.role && contributor.role.name ? String(contributor.role.name).toLowerCase() : "";
        return contributorRoleId === roleId || contributorRoleName === roleId;
      })
      .map((contributor) => contributor.contributor && contributor.contributor.name ? contributor.contributor.name : "")
      .filter(Boolean)
      .join(" | ");
  };

  const getPublisherNames = (publishers) => {
    return (publishers || [])
      .map((publisher) => publisher.publisher && publisher.publisher.name ? publisher.publisher.name : "")
      .filter(Boolean)
      .join(" | ");
  };

  const getPublisherAuthors = (publishers) => {
    return (publishers || [])
      .map((publisher) => publisher.author || "")
      .filter(Boolean)
      .join(" | ");
  };

  const getExclusiveStoreLabel = (exclusiveShop) => {
    const exclusiveStoreOptions = {
      3: "Beatport 4 weeks",
      4: "Beatport 2 weeks",
      5: "Traxsource 2 weeks",
      11: "Pre-order 2 weeks",
      14: "Pre-order 1 weeks"
    };

    return exclusiveStoreOptions[exclusiveShop] || "";
  };

  const getTerritoriesValue = (releaseData) => {
    if (releaseData.territory === "worldwide") {
      return "WW";
    }

    if (Array.isArray(releaseData.countries) && releaseData.countries.length > 0) {
      return releaseData.countries.join(" | ");
    }

    return "";
  };

  const exportLabelCopy = async () => {
    try {
      setExportingLabelCopy(true);

      const requestConfig = {
        headers: {
          Authorization: `Bearer ${tokenAPI}`,
          "x-api-key": X_API_KEY,
          "Content-Type": "application/json"
        }
      };
      const [releaseResponse, genresResponse] = await Promise.all([
        axios.get(`${API_URL}releases/${id}/`, requestConfig),
        axios.get(`${API_URL}genres/`, requestConfig)
      ]);

      const releaseData = releaseResponse.data || {};
      const genres = genresResponse.data && genresResponse.data.results ? genresResponse.data.results : [];
      const releaseArtists = releaseData.artists || [];
      const trackDetailsResponses = await Promise.all(
        (releaseData.tracks || []).map((track) => {
          if (!track.id) {
            return Promise.resolve({ data: track });
          }

          return axios.get(`${API_URL}tracks/${track.id}/`, requestConfig);
        })
      );
      const tracks = trackDetailsResponses.length > 0
        ? trackDetailsResponses.map((response) => response.data || {})
        : [{}];
      const headers = [
        "Release Name",
        "Release Version",
        "Language",
        "Label",
        "Copyright (P)",
        "Copyright (P) Year",
        "Copyright (C)",
        "Copyright (C) Year",
        "Release Type",
        "Release Primary Genre",
        "Sub Genre",
        "Catalogue Number",
        "Back Catalog",
        "Official Release Date",
        "Exclusive store release date",
        "Exclusive Store and Duration",
        "Territories",
        "UPC/EAN",
        "Main Release Artist Name",
        "Feature Release Artist Name",
        "Track Name",
        "Album Only",
        "Track Primary Genre",
        "Track Sub Genre",
        "Mix Name",
        "Track Language",
        "Vocals",
        "ISRC",
        "Copyright (C)",
        "Copyright (C) Year",
        "Explicit Lyrics",
        "Main Track Artists",
        "Track Remixers",
        "Track Feature Artists",
        "Contributor Composer",
        "Contributor Lyricist",
        "Track Publisher",
        "Author Songwriter Full Name",
        "Contributor Arranger",
        "Contributor Conductor",
        "Contributor Engineer",
        "Contributor Mixer",
        "Contributor Orchestra",
        "Contributor Producer"
      ];
      const rows = tracks.map((track) => [
        releaseData.name || name || "",
        releaseData.release_version || "",
        releaseData.language || "",
        releaseData.label && releaseData.label.name ? releaseData.label.name : "",
        releaseData.copyright_holder || "",
        releaseData.copyright_holder_year || "",
        releaseData.publisher || "",
        releaseData.publisher_year || "",
        releaseData.kind || "",
        getGenreName(releaseData.genre, genres),
        getSubgenreName(releaseData.genre, releaseData.subgenre, genres),
        releaseData.catalogue_number || "",
        toCsvBoolean(releaseData.backcatalog),
        releaseData.official_date || "",
        toCsvBoolean(Boolean(releaseData.exclusive_date)),
        getExclusiveStoreLabel(releaseData.exclusive_shop),
        getTerritoriesValue(releaseData),
        releaseData.ean || "",
        getArtistNamesByKind(releaseArtists, "main"),
        getArtistNamesByKind(releaseArtists, "featuring"),
        track.name || "",
        toCsvBoolean(track.album_only),
        getGenreName(track.genre, genres),
        getSubgenreName(track.genre, track.subgenre, genres),
        track.mix_name || "",
        track.language || "",
        track.vocals || "",
        track.ISRC || "",
        track.copyright_holder || "",
        track.copyright_holder_year || "",
        toCsvBoolean(track.explicit_content),
        getArtistNamesByKind(track.artists, "main"),
        getArtistNamesByKind(track.artists, "remixer"),
        getArtistNamesByKind(track.artists, "featuring"),
        getContributorNamesByRole(track.contributors, "composer"),
        getContributorNamesByRole(track.contributors, "lyricist"),
        getPublisherNames(track.publishers),
        getPublisherAuthors(track.publishers),
        getContributorNamesByRole(track.contributors, "arranger"),
        getContributorNamesByRole(track.contributors, "conductor"),
        getContributorNamesByRole(track.contributors, "engineer"),
        getContributorNamesByRole(track.contributors, "mixer"),
        getContributorNamesByRole(track.contributors, "orchestra"),
        getContributorNamesByRole(track.contributors, "producer")
      ]);
      const csvContent = [
        headers.map(escapeCsvValue).join(","),
        ...rows.map((row) => row.map(escapeCsvValue).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const downloadLink = document.createElement("a");
      const safeReleaseName = (releaseData.name || name || "release")
        .trim()
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();

      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.setAttribute("download", `${safeReleaseName || "release"}-label-copy.csv`);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error("Failed to export label copy", error);
    } finally {
      setExportingLabelCopy(false);
    }
  };

  return (
    <DropdownItem
      style={{ cursor: exportingLabelCopy ? "default" : "pointer" }}
      onClick={exportLabelCopy}
      disabled={exportingLabelCopy}
    >
      {exportingLabelCopy ? "Exporting Label Copy..." : "Export Label Copy"}
    </DropdownItem>
  );
};

export default ExportLabelCopy;
